import { create } from 'zustand';
import { formatBytes } from '../chat/labels.js';
import { api, errorMessage } from '../lib/api/index.js';
import { randomId } from '../lib/randomId.js';
import { currentSettings } from '../settings/settingsStore.js';

/**
 * Files on their way up to the server.
 *
 * An upload is complete when the server has stored it in memory and posted the
 * file message - which reaches this client over the socket like any other
 * message, so a finished upload simply disappears from here.
 *
 * The XHR handles live outside the store: they are not state, and nothing
 * renders from them.
 */

export interface Upload {
  id: string;
  channelId: string;
  name: string;
  size: number;
  /** 0..1 */
  progress: number;
  status: 'uploading' | 'failed';
  error: string | null;
}

interface UploadState {
  uploads: Upload[];
  /** Starts uploading each file to the channel. Too-large files fail at once. */
  start: (channelId: string, files: readonly File[]) => void;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
  reset: () => void;
}

/** Progress repaints at most this often; raw XHR progress fires far faster. */
const PROGRESS_INTERVAL_MS = 100;

const cancels = new Map<string, () => void>();

export const useUploadStore = create<UploadState>((set, get) => {
  const patch = (id: string, changes: Partial<Upload>): void =>
    set((state) => ({
      uploads: state.uploads.map((upload) =>
        upload.id === id ? { ...upload, ...changes } : upload,
      ),
    }));

  const remove = (id: string): void => {
    cancels.delete(id);
    set((state) => ({ uploads: state.uploads.filter((upload) => upload.id !== id) }));
  };

  return {
    uploads: [],

    start(channelId: string, files: readonly File[]): void {
      const max = currentSettings().maxFileTransferBytes;

      for (const file of files) {
        const id = randomId();
        const tooLarge = file.size > max;
        const empty = file.size === 0;

        set((state) => ({
          uploads: [
            ...state.uploads,
            {
              id,
              channelId,
              name: file.name,
              size: file.size,
              progress: 0,
              status: tooLarge || empty ? 'failed' : 'uploading',
              error: tooLarge
                ? `Larger than the ${formatBytes(max)} limit`
                : empty
                  ? 'The file is empty'
                  : null,
            },
          ],
        }));

        if (tooLarge || empty) {
          continue;
        }

        let lastPaint = 0;
        const handle = api.files.uploadFile(channelId, file, (fraction) => {
          const now = Date.now();

          if (now - lastPaint >= PROGRESS_INTERVAL_MS || fraction >= 1) {
            lastPaint = now;
            patch(id, { progress: fraction });
          }
        });

        cancels.set(id, handle.cancel);

        handle.done.then(
          () => remove(id),
          (error: unknown) => {
            if (error instanceof DOMException && error.name === 'AbortError') {
              remove(id);
              return;
            }

            cancels.delete(id);
            patch(id, { status: 'failed', error: errorMessage(error) });
          },
        );
      }
    },

    cancel(id: string): void {
      const cancel = cancels.get(id);

      if (cancel) {
        cancel();
      } else {
        get().dismiss(id);
      }
    },

    dismiss(id: string): void {
      remove(id);
    },

    reset(): void {
      for (const cancel of cancels.values()) {
        cancel();
      }

      cancels.clear();
      set({ uploads: [] });
    },
  };
});

/** Stable empty list, for a selector with nothing uploading. */
export const EMPTY_UPLOADS: readonly Upload[] = [];
